
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { CceSubCategorieObject } from '@/store/modules/CCE/SubCategoriesModule';

  export default defineComponent({
    name: 'cce-sub-categories-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const { can } = useAbility();
      const selectedCategory = ref<number>(0);
      const submitSubCategoryOptionstButton = ref<HTMLButtonElement | null>(
        null
      );
      const createdSubCategory = ref<CceSubCategorieObject | null>(null);

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const cceSubCategorieSchema = Yup.object().shape({
        titleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        titleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
      });

      const onSubmitCreate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }
        const result = await store.dispatch(
          Actions.CREATE_CCE_SUB_CATEGORIE,
          values
        );
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          createdSubCategory.value = result;
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_CCE_SUB_CATEGORIE'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('cceSubCategoriesCreating'), [
          translate('cce'),
        ]);
      });
      await store.dispatch(Actions.GET_ALL_CCE_CATEGORIES);
      await store.dispatch(Actions.GET_ALL_CCE_QUALITY);

      const subOptions = store.getters.allCceQualityList.map((p) => ({
        qualityId: p.id,
        title: p.title,
        price: null,
      }));

      const onSubmitSubCategoryOptions = async () => {
        const subCategoriesOptions = subOptions.reduce(
          (previousValue, currentValue) => {
            previousValue.push({
              qualityId: currentValue.qualityId,
              price: currentValue.price,
            });
            return previousValue;
          },
          []
        );

        const payload = {
          subCategoryId: createdSubCategory.value?.id,
          subCategoriesOptions,
        };

        if (submitSubCategoryOptionstButton.value) {
          // eslint-disable-next-line
          submitSubCategoryOptionstButton.value!.disabled = true;
          submitSubCategoryOptionstButton.value.setAttribute(
            'data-kt-indicator',
            'on'
          );
        }

        await store.dispatch(Actions.UPDATE_CCE_SUB_CATEGORIE_OPTIONS, payload);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitSubCategoryOptionstButton.value?.removeAttribute(
            'data-kt-indicator'
          );
          // eslint-disable-next-line
          submitSubCategoryOptionstButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_ADDED_SUB_CATEGORIE_OPTIONS'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitSubCategoryOptionstButton.value?.removeAttribute(
              'data-kt-indicator'
            );
            // eslint-disable-next-line
            submitSubCategoryOptionstButton.value!.disabled = false;

            router.push({ name: 'cce-sub-categories-listing' });
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        cceQualitys: computed(() => store.getters.allCceQualityList),
        cceCategories: computed(() => store.getters.allCceCategoriesList),
        onSubmitCreate,
        onSubmitSubCategoryOptions,
        submitButton,
        translate,
        cceSubCategorieSchema,
        createdSubCategory,
        goBack,
        selectedCategory,
        can,
        subOptions,
      };
    },
  });
